<template>
  <BaseModule
    v-bind="$props"
    class="thumbs-gallery"
    data-module="thumbs-gallery"
  >
    <div
      class="container"
      :style="{
        '--text-position': textPosition,
      }"
    >
      <div class="thumbs-gallery__content half-width">
        <header
          v-if="title || subtitle"
          class="base-title-fontsize"
        >
          <component
            :is="titleTag"
            v-if="title"
            class="tit"
          >
            {{ title }}
          </component>
          <h3
            v-if="subtitle"
            class="sub"
          >
            {{ subtitle }}
          </h3>
        </header>
        <div
          v-if="text"
          class="txt"
          v-html="text"
        ></div>
        <footer
          v-if="buttons.length"
          class="thumbs-gallery__buttons"
        >
          <ul>
            <li
              v-for="(button, index) in buttons"
              :key="index"
            >
              <EditorLink
                v-if="button.buttonText"
                :model="button.buttonUrl"
                class="cta button"
                :class="{ 'button--featured': button.buttonFeatured }"
              >
                {{ button.buttonText }}
              </EditorLink>
            </li>
          </ul>
        </footer>
      </div>

      <div class="thumbs-gallery__gallery half-width">
        <div class="aspect-ratio-parent">
          <Loader
            v-if="loading && allSlides.length < 1"
            class="aspect-ratio-child"
          ></Loader>
          <div
            v-else
            class="aspect-ratio-child"
          >
            <div
              ref="swiperTop"
              class="swiper gallery-top"
            >
              <!-- Additional required wrapper -->
              <div class="swiper-wrapper">
                <div
                  v-for="(slide, idx) in allSlides"
                  :key="slide.image || idx"
                  class="swiper-slide"
                >
                  <iframe
                    v-if="slide.type === 'video'"
                    :src="
                      youtubeEmbed({
                        url: slide.videoSrc,
                        controls: !slide.hideYoutubeControls,
                      })
                    "
                  ></iframe>
                  <LImage
                    v-else
                    class="absolute-full"
                    :src="
                      isMobile && slide.differentMobileBackgroundImage
                        ? slide.imageMobile
                        : slide.image
                    "
                    :alt="
                      isMobile &&
                      slide.differentMobileBackgroundImage &&
                      slide.mobileAltText
                        ? slide.mobileAltText
                        : slide.altText
                    "
                  />
                </div>
              </div>

              <!-- If we need navigation buttons -->
              <div
                v-show="hasMultipleSlides"
                :class="`swiper-button-prev btn-prev-${instanceId}`"
              ></div>
              <div
                v-show="hasMultipleSlides"
                :class="`swiper-button-next btn-next-${instanceId}`"
              ></div>
            </div>

            <div
              ref="swiperThumbs"
              thumbsSlider=""
              class="swiper gallery-bottom"
            >
              <!-- Additional required wrapper -->
              <div class="swiper-wrapper">
                <div
                  v-for="(slide, idx) in allSlides"
                  :key="slide.image || idx"
                  class="swiper-slide"
                >
                  <LImage
                    v-if="slide.type === 'video'"
                    class="absolute-full"
                    :src="youtubeThumb(slide.videoSrc)"
                  ></LImage>
                  <LImage
                    v-else
                    class="absolute-full"
                    :src="
                      isMobile && slide.differentMobileBackgroundImage
                        ? slide.imageMobile
                        : slide.image
                    "
                    :alt="
                      isMobile &&
                      slide.differentMobileBackgroundImage &&
                      slide.mobileAltText
                        ? slide.mobileAltText
                        : slide.altText
                    "
                  />
                </div>
              </div>

              <div
                v-show="hasMultipleSlides"
                class="swiper-button-prev thumb-prev"
              ></div>
              <div
                v-show="hasMultipleSlides"
                class="swiper-button-next thumb-next"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import base from '@/mixins/base';

import getApiHotels from '@/mixins/getApiHotels';
import Swiper from 'swiper';
import { Autoplay, Navigation, Thumbs } from 'swiper/modules';
import Loader from '@/components/Loader.vue';
import { youtubeEmbed, youtubeThumb } from '@/helpers/youtube';
import BaseModule from './generic/BaseModule.vue';
import EditorLink from './EditorLink.vue';

export default {
  name: 'ThumbsGallery',
  components: {
    BaseModule,
    EditorLink,
    Loader,
  },
  mixins: [getApiHotels, base],
  props: {
    ...BaseModule.props,
    hotelId: {
      type: Number,
      default: null,
    },
    useHotelGallery: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      default: '',
    },
    textPosition: {
      type: String,
      default: 'row-reverse',
    },
    buttons: {
      type: Array,
      default() {
        return [];
      },
    },
    slides: {
      type: Array,
      default() {
        return [];
      },
    },
    swiperAutoplay: {
      type: Boolean,
      default: false,
    },
    swiperAutoplaySpeed: {
      type: Number,
      default: 10,
    },
  },
  data() {
    return {
      mobileMaxWidth: 768,
      loading: false,
      swiper: null,
      thumbs: null,
      isMobile: false,
    };
  },
  computed: {
    selectedHotel() {
      return this.hotelId
        ? this.hotels.find((hotel) => hotel.Codigo === this.hotelId)
        : null;
    },
    allSlides() {
      const slides = [...this.slides];
      const hotelImages = this.selectedHotel?.Imagens;

      if (this.useHotelGallery && hotelImages) {
        if (hotelImages.ImagemPrincipal) {
          slides.push({
            image: hotelImages.ImagemPrincipal,
            imageMobile:
              hotelImages.ImagemPrincipalMobile || hotelImages.ImagemPrincipal,
          });
        }

        if (hotelImages.Galeria) {
          hotelImages.Galeria.forEach((image) => {
            slides.push({
              image,
              imageMobile: image,
            });
          });
        }
      }

      return slides;
    },
    hasMultipleSlides() {
      return this.allSlides.length > 1;
    },
    editing() {
      return (
        typeof this.$parent === 'undefined' ||
        typeof this.$parent.$parent === 'undefined'
      );
    },
    swiperAutoplaySettings() {
      if (!this.editing && this.swiperAutoplay && this.swiperAutoplaySpeed) {
        return {
          delay: this.swiperAutoplaySpeed * 1000,
        };
      }
      return false;
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
    ...mapGetters(['hasLetsbookIntegration']),
  },
  watch: {
    allSlides() {
      if (this.swiper || this.thumbs) {
        this.swiperDestroy();
      }

      if (this.hasMultipleSlides) {
        this.$nextTick(() => {
          this.swiperInit();
        });
      }
    },
    currentEditableItem() {
      this.slideToActivatedItem();
    },
  },
  async mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);

    if (this.hasMultipleSlides) {
      this.swiperInit();
    }

    if (this.hasLetsbookIntegration) {
      this.loading = true;
      try {
        await this.getApiHotels();
      } catch (err) {
        console.warn(err);
      }
      this.loading = false;
    }
  },
  methods: {
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('slides')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper) {
            this.swiper.slideTo(itemPathIndex);
          }
        }
      }
    },
    swiperInit() {
      this.thumbs = new Swiper(this.$refs.swiperThumbs, {
        modules: [Navigation],
        spaceBetween: 12,
        slidesPerView: 'auto',
        freeMode: true,
        watchSlidesProgress: true,
        centerInsufficientSlides: true,
        slidesPerGroup: 3,
        navigation: {
          nextEl: '.thumb-next',
          prevEl: '.thumb-prev',
        },
      });

      this.swiper = new Swiper(this.$refs.swiperTop, {
        modules: [Autoplay, Navigation, Thumbs],
        spaceBetween: 12,
        autoplay: this.swiperAutoplaySettings,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        thumbs: {
          swiper: this.thumbs,
        },
      });

      this.slideToActivatedItem();
    },
    swiperDestroy() {
      this.thumbs.destroy();
      this.thumbs = null;
      this.swiper.destroy();
      this.swiper = null;
    },
    onResize() {
      const isMobile = window.innerWidth < this.mobileMaxWidth;

      // If "device" changed...
      if (isMobile !== this.isMobile) {
        this.isMobile = isMobile;
      }
    },
    youtubeEmbed,
    youtubeThumb,
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'text',
                type: 'Html',
                label: 'Texto de apoio',
                wrapWithParagraph: false,
              },
              {
                name: 'textPosition',
                type: 'VueSelect',
                label: 'Posição do texto',
                options: [
                  {
                    label: 'Esquerda',
                    value: 'row',
                  },
                  {
                    label: 'Direita',
                    value: 'row-reverse',
                  },
                ],
              },
            ],
          },
          {
            name: 'gallery',
            label: 'Galeria',
            fields: [
              {
                name: 'slides',
                type: 'List',
                itemLabel: 'Mídia {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar mídia',
                fields: [
                  {
                    name: 'type',
                    type: 'VueSelect',
                    label: 'Tipo de mídia',
                    default: 'image',
                    options: [
                      {
                        label: 'Imagem',
                        value: 'image',
                      },
                      {
                        label: 'Vídeo',
                        value: 'video',
                      },
                    ],
                  },
                  {
                    name: 'videoSrc',
                    type: 'Url',
                    label: 'URL do vídeo',
                    availableTypes: ['externalLink'],
                    allowNewTab: false,
                    isEnabled() {
                      return this.type === 'video';
                    },
                  },
                  {
                    name: 'hideYoutubeControls',
                    type: 'Checkbox',
                    default: false,
                    label: 'Ocultar controles de vídeo',
                    isEnabled() {
                      return this.type === 'video';
                    },
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem',
                    helpText: 'Dimensões recomendadas: 960x520',
                    isEnabled() {
                      return this.type !== 'video';
                    },
                  },
                  {
                    name: 'altText',
                    type: 'ShortText',
                    label: 'Texto alternativo',
                    isEnabled() {
                      return this.type !== 'video';
                    },
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                    isEnabled() {
                      return this.type !== 'video';
                    },
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    maxWidth: 960,
                    label: 'Imagem mobile',
                    helpText: 'Dimensões recomendadas: 768x768',
                    isEnabled() {
                      return (
                        this.type !== 'video' &&
                        this.differentMobileBackgroundImage
                      );
                    },
                  },
                  {
                    name: 'altTextMobile',
                    type: 'ShortText',
                    label: 'Texto alternativo mobile',
                    isEnabled() {
                      return (
                        this.type !== 'video' &&
                        this.differentMobileBackgroundImage
                      );
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configurações',
            fields: [
              {
                name: 'useHotelGallery',
                type: 'Checkbox',
                label: 'Mostrar galeria do hotel',
                isEnabled({ hasLetsbookIntegration }) {
                  return hasLetsbookIntegration;
                },
              },
              {
                name: 'hotelId',
                type: 'VueSelect',
                label: 'Selecione o hotel',
                isEnabled({ useHotelGallery }) {
                  return useHotelGallery;
                },
                validation: {
                  presence: {
                    allowEmpty: false,
                    message: 'Selecione o hotel para integração',
                  },
                },
                options() {
                  const hotelsToOptions = (hotels) =>
                    hotels.map((hotel) => ({
                      label: hotel.Nome,
                      value: hotel.Codigo,
                    }));

                  // Are hotels already loaded?
                  if (this.hotels.length > 0) {
                    return hotelsToOptions(this.hotels);
                  }

                  // THIS IS PURE MAGIC
                  return new Promise((resolve) => {
                    const unwatch = this.$watch('hotels', (hotels) => {
                      resolve(hotelsToOptions(hotels));
                      unwatch();
                    });
                  });
                },
              },
              {
                name: 'swiperAutoplay',
                type: 'Checkbox',
                label: 'Habilitar transição automática das imagens (autoplay)',
                isEnabled({ useFixedText }) {
                  return !useFixedText;
                },
              },
              {
                name: 'swiperAutoplaySpeed',
                type: 'Range',
                label: 'Velocidade da transição das imagens',
                min: 1,
                max: 30,
                step: 1,
                format: {
                  suffix: 's',
                },
                isEnabled({ swiperAutoplay, useFixedText }) {
                  return swiperAutoplay || useFixedText;
                },
              },
            ],
          },
          {
            name: 'buttons',
            label: 'Botões',
            fields: [
              {
                type: 'List',
                name: 'buttons',
                maxItems: 2,
                itemLabel: 'Botão {index}',
                addItemLabel: 'Adicionar botão',
                fields: [
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 15,
                  },
                  {
                    name: 'buttonUrl',
                    type: 'Url',
                  },
                  {
                    name: 'buttonFeatured',
                    type: 'Checkbox',
                    label: 'Destacar botão',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.thumbs-gallery {
  .container {
    display: flex;
    width: 100%;
    gap: 4vw;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0 4vw;
    padding-top: var(--padding-top);
    padding-bottom: var(--padding-bottom);
    flex-direction: var(--text-position);
  }

  &__gallery {
    .aspect-ratio-parent {
      width: 100%;
      padding-top: 70.31%;
      position: sticky;
      top: 20px;

      .aspect-ratio-child {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;

        .swiper {
          width: 100%;
          margin-left: auto;
          margin-right: auto;

          .swiper-slide {
            background-size: cover;
            background-position: center;
            text-align: center;
            font-size: 18px;
            background: #fff;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 8px;
            overflow: hidden;

            iframe {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              border: 0;
            }
          }

          .swiper-slide img {
            display: block;
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .swiper-button-prev,
          .swiper-button-next {
            width: 40px;

            @include media-breakpoint-up(sm) {
              width: 60px;
            }

            &::after {
              background-color: rgba($lb-black, 0.333);
              color: $lb-white;
              border-radius: 50px;
              width: 30px;
              height: 30px;
              font-size: 10px;
              display: flex;
              justify-content: center;
              align-items: center;

              @include media-breakpoint-up(sm) {
                width: 50px;
                height: 50px;
                font-size: 30px;
              }
            }
          }
        }

        .gallery-top {
          height: 80%;
          width: 100%;
        }

        .gallery-bottom {
          height: calc(20% - 10px);
          box-sizing: border-box;
          margin-top: 10px;

          .swiper-slide {
            width: 22%;
            opacity: 0.4;
            cursor: pointer;
          }

          .swiper-button-prev.thumb-prev,
          .swiper-button-next.thumb-next {
            --swiper-navigation-sides-offset: 0px;
            width: 28px;
            &::after {
              width: 28px;
              height: 28px;
              font-size: 14px;
            }

            &.swiper-button-disabled {
              display: none;
            }
          }

          .swiper-slide-thumb-active {
            opacity: 1;
          }
        }
      }
    }
  }

  .half-width {
    width: 50%;
  }

  &__buttons {
    ul {
      margin-top: 24px;
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }

    .button {
      min-height: 48px;
      font-size: var(--content-size-desk, $size-normal);
      line-height: 1.2;
      min-width: 200px;
      border: 2px solid var(--button-color, $primary);
      color: var(--button-color, $primary);
      background-color: transparent;
      border-radius: 8px;
      overflow: hidden;

      &--featured {
        color: var(--button-text-color, $primary);
        background-color: var(--button-color, $primary);
        border: none;
      }
    }
  }
}

@include media-breakpoint-down(md) {
  .thumbs-gallery {
    .container {
      flex-direction: column;
      gap: 32px;
      padding: 0 20px;
      padding-top: var(--padding-top-mobile);
      padding-bottom: var(--padding-bottom-mobile);
    }

    &__gallery {
      .aspect-ratio-parent {
        position: relative;
        top: unset;
        padding-top: 125%;

        .aspect-ratio-child {
          .gallery-bottom {
            .swiper-slide {
              width: 25%;
              border-radius: 4px;
            }
          }
        }
      }
    }

    .half-width {
      width: 100%;
    }

    &__buttons {
      ul {
        flex-direction: column;
      }

      .button {
        font-size: var(--content-size-mobile, $size-normal);
      }
    }
  }
}
</style>
